import { VuexModule, Module, Action, Mutation } from "vuex-module-decorators";
import Store from "@/store";
import { rawApi } from "@/lib";
import { CurrentUserResource } from "@/models/api/users/CurrentUserResource";
import { SavedOrderSearchListResource } from "@/models/api/users/SavedOrderSearchListResource";
import { GetTableColumnsResponse } from "@/models/api/account/tableColumns";
import { GetUserSettingsResponse } from "@/models/api/account/userSettings";
import { IAccountStore, IAdHocFilter } from "@/store/types";

const name = "account";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class Account extends VuexModule implements IAccountStore {
  permissions: string[] = [];
  orderSearches: any[] = [];
  lastOrderSearch: any = null;
  lastSearchSubscription: any = null;
  subscribedOrderSearches: any[] = [];
  selectedSavedOrderSearchId: number = null;
  adhocFilter: IAdHocFilter = null;
  dispatchTableColumnPreferences: any = null;
  homeFacilitiesUserSettings: string[] = null;
  truckImportFormatUserSettings: string[] = null;
  truckImportFacilityCodeUserSettings: string[] = null;
  deliveryExportFormatUserSettings: string[] = null;
  deliveryExportFacilityCodeUserSettings: string[] = null;
  truckFilterFacilityCodeUserSettings: string[] = null;
  defaultSavedSearchesUserSettings: string[] = null;
  defaultSearchSubscriptionsUserSettings: string[] = null;
  barcodeSystemCurrentFacilityCodeUserSettings: string[] = null;
  timeFrameTypeUserSettings: string[] = null;
  timeFrameStartUserSettings: string[] = null;
  timeFrameOriginUserSettings: string[] = null;

  @Action
  async ensureLoadColumnPreferencesForTable(tableName: string): Promise<void> {
    if (this[tableName + "ColumnPreferences"]) return;

    try {
      const response = await rawApi.get<GetTableColumnsResponse>(
        `/account/tableColumns/${tableName}`
      );

      let columns = null;
      if (!response.data || !response.data.columnNames) {
        columns = this.context.rootGetters["order/getAvailableOrderFields"].map(x => x.key);
      } else {
        columns = response.data.columnNames;
      }

      this.context.commit("setColumnPreferences", { name: tableName, columns: columns });
    } catch (ex) {
      console.error(ex);
    }
  }

  @Action
  setUserFacilities(facilities){
    this.context.commit("mutateUserFacilities", facilities);
  }

  @Action
  async loadUserSettings(settingName: string): Promise<void> {
    if (this[settingName + "UserSettings"]) return;

    try {
      const response = await rawApi.get<GetUserSettingsResponse>(
        `/account/userSettings/${settingName}`
      );

      let values = null;
      if (response.data && response.data.settingValues) {
        values = response.data.settingValues;
      }

      this.context.commit("setUserSettings", { name: settingName, values: values });
    } catch (ex) {
      console.error(ex);
    }
  }

  @Action
  async updateColumnPreferences(request: any): Promise<void> {
    if (!request.tableName || !request.columns) return;

    this.context.commit("setColumnPreferences", {
      name: request.tableName,
      columns: request.columns
    });

    try {
      await rawApi.put<any>(`/account/tableColumns/${request.tableName}`, {
        columnNames: request.columns
      });
    } catch (ex) {
      console.error("Failed to save column preferences to server", ex);
    }
  }

  @Action
  async updateUserSettings(request: any): Promise<void> {
    if (!request.settingName) return;

    this.context.commit("setUserSettings", { name: request.settingName, values: request.values });

    try {
      await rawApi.put<any>(`/account/userSettings/${request.settingName}`, {
        settingValues: request.values
      });
    } catch (ex) {
      console.error("Failed to save user settings to server", ex);
    }
  }

  @Action
  getUserDetails(): Promise<CurrentUserResource> {
    return rawApi.get<CurrentUserResource>("/user").then(response => {
      this.setPermissions(response.data.permissions);
      return response.data;
    });
  }

  @Action
  getSavedOrderSearches(): Promise<Array<SavedOrderSearchListResource>> {
    return rawApi
      .get<Array<SavedOrderSearchListResource>>("/account/orderSearches")
      .then(response => {
        if (response.data) {
            response.data.map(data => {
            data.searchFields.map(x => {
              if (x.name.includes("Date")) {
                if (x.filterValues[0].length === 23) {
                  x.dateType = "fixed";
                } else {
                  x.dateType = "dynamic";
                }
              }
              if (x.name.includes("DeliveryForIds")) {
                x.filterValues = x.filterValues.map(x => Number(x))
              }
            });
          });
          this.setOrderSearches(response.data);
          return response.data;
        } else {
          this.setOrderSearches([]);
          return [];
        }
      });
  }

  @Action
  saveOrderSearch(payload: any): Promise<void> {
    if (payload.id) return rawApi.put(`/account/orderSearches/${payload.id}`, payload);
    return rawApi.post("/account/orderSearches", payload);
  }

  @Action
  deleteOrderSearch(searchId: number): Promise<void> {
    return rawApi.delete(`/account/orderSearches/${searchId}`).then(() => {
      this.removeSavedOrderSearch(searchId);
      return;
    });
  }

  @Action
  subscribeToFaciltiySearch(searchId: number): Promise<void> {
    return rawApi.post(`/facilityOrderSearches/${searchId}/subscribe`).then(() => {
      this.context.dispatch(
        "facility/updateSearchSubscription",
        { searchId: searchId, subscribed: true },
        { root: true }
      );
      return;
    });
  }

  @Action
  unsubscribeToFaciltiySearch(searchId: number): Promise<void> {
    return rawApi.post(`/facilityOrderSearches/${searchId}/unsubscribe`).then(() => {
      this.context.dispatch(
        "facility/updateSearchSubscription",
        { searchId: searchId, subscribed: false },
        { root: true }
      );
      this.removeSubscribedOrderSearch(searchId);
      return;
    });
  }

  @Action
  deleteSubscribedOrderSearch(searchId: number): void {
    this.removeSubscribedOrderSearch(searchId);
  }

  @Action
  updateSubscribedSearches(searches) {
    this.setSubscribedOrderSearches(searches);
  }

  @Mutation
  setColumnPreferences(payload: any) {
    this[payload.name + "ColumnPreferences"] = payload.columns;
  }

  @Mutation
  setUserSettings(payload: any) {
    this[payload.name + "UserSettings"] = payload.values;
  }

  @Mutation
  mutateUserFacilities(facilities: string) {
    this.homeFacilitiesUserSettings = facilities?facilities.split(','):[];
  }

  @Mutation
  setPermissions(permissions: string[]) {
    this.permissions = permissions;
  }

  @Mutation
  setOrderSearches(searches) {
    this.orderSearches =  searches.map(os=>{
      return {
        id: os.id,
        searchName: os.searchName,
        searchFields: os.searchFields.map(sf=>{
          if(sf.name == "RefNumbers"){
            return {
              name: sf.name,
              isDefault: sf.isDefault,
              filterValues: sf.filterValues.map(fv=>JSON.parse(fv))
            }
          }
          return sf
        })
      }
    });;
  }

  @Mutation
  setLastOrderSearch(search) {
    this.lastOrderSearch = search;
  }

  @Mutation
  setLastSearchSubscription(search) {
    this.lastSearchSubscription = search;
  }

  @Mutation
  setSubscribedOrderSearches(searches) {
    this.subscribedOrderSearches = searches;
  }

  @Mutation
  removeSavedOrderSearch(searchId) {
    let index = this.orderSearches.findIndex(x => x.id === searchId);
    this.orderSearches.splice(index, 1);
  }

  @Mutation
  removeSubscribedOrderSearch(searchId) {
    let index = this.subscribedOrderSearches.findIndex(x => x.id === searchId);
    this.subscribedOrderSearches.splice(index, 1);
  }

  @Mutation
  setSelectedSavedOrderSearch(value) {
    this.selectedSavedOrderSearchId = value;
  }

  @Mutation
  setAdhocFilter(filter: IAdHocFilter) {
    this.adhocFilter = filter;
    this.selectedSavedOrderSearchId = -1;
  }

  @Mutation
  resetAdhocFilter() {
    this.adhocFilter = null;

    if (this.selectedSavedOrderSearchId == -1) this.selectedSavedOrderSearchId = null;
  }

  @Action
  setAlertTypeFilter(payload: any) {
    if (!payload || !payload.alertType) return;

    var searchFields = [{ name: "AlertTypes", filterValues: [payload.alertType] }]

    if(payload.alertType == "ordersWithMissingPod")
      searchFields.push({name:"OrderStatuses", filterValues:["CMP"]})

    var filter = {
      id: null,
      searchName: null,
      searchFields
    };

    if (payload.facilityCodes)
      if (Array.isArray(payload.facilityCodes))
        filter.searchFields.push({ name: "FacilityCodes", filterValues: payload.facilityCodes });
      else
        filter.searchFields.push({ name: "FacilityCodes", filterValues: [payload.facilityCodes] });

    if (payload.deliveryForIds) {
      if (Array.isArray(payload.deliveryForIds))
        filter.searchFields.push({ name: "DeliveryForIds", filterValues: payload.deliveryForIds.map(x=>Number(x)) });
      else
        filter.searchFields.push({ name: "DeliveryForIds", filterValues: [Number(payload.deliveryForIds)] });
    }

    this.setAdhocFilter(filter);
  }

  get getSavedOrderSearchList(): Array<any> {
    const searchList = [];
    if (this.hasAdhocFilter) {
      searchList.push({ text: "(adhoc)", value: -1 });
    }
    searchList.push(...this.orderSearches.map(x => ({ text: x.searchName, value: x.id })));
    searchList.push(
      ...this.subscribedOrderSearches.map(x => ({
        text: x.searchName + " [" + x.facilityCode + "] (subscribed)",
        value: (x.id + 1) * -1
      }))
    );

    return searchList;
  }

  get getSelectedSavedOrderSearch(): any {
    if (this.selectedSavedOrderSearchId && this.selectedSavedOrderSearchId < -1)
      return this.subscribedOrderSearches.find(
        x => x.id == (this.selectedSavedOrderSearchId + 1) * -1
      );
    return this.selectedSavedOrderSearchId
      ? this.orderSearches.find(x => x.id == this.selectedSavedOrderSearchId)
      : null;
  }

  get getUserPermissions(): string[] {
    return this.permissions;
  }

  get hasAdhocFilter(): boolean {
    return this.adhocFilter !== null;
  }

  get getAdhocFilterIfActive(): any {
    if (!this.hasAdhocFilter) return null;

    if (this.selectedSavedOrderSearchId != -1 && this.selectedSavedOrderSearchId != null) return null;

    return this.adhocFilter;
  }
}
